import { ServiceException } from '@inter-ikea-kompis/exceptions';
import { IframeUtility, LocaleUtility } from '@inter-ikea-kompis/utilities';
import randomBytes from '@frando/randombytes';
import IframeManager from '../../../utilities/IframeManager.js';
import ServiceFetch from '../../../utilities/ServiceFetch.js';
import AccountServiceEnum from './enums/AccountServiceEnum.js';
import DexfAccountServiceStorage from './DexfAccountServiceStorage.js';
import DexfAccountServiceUtility from './DexfAccountServiceUtility.js';
/**
 * This service is used for handling authentication with DEXF AccountService.
 */
export default class DexfAccountService {
    /**
     * Initializes a new instance of DexfAccountService.
     *
     * @param options Configuration options for DexfAccountService.
     * @param options.accessTokenUrl URL to get the access token.
     * @param options.authenticationUrl URL for authentication.
     * @param options.clientId Client ID for authentication.
     * @param options.locale Current locale.
     * @param options.loginOptions [options={}] The options for login. Optional.
     * @param options.logoutUri URL for logout.
     * @param options.logoutRedirectUri Redirect URI after logout.
     * @param options.redirectUri Redirect URI after authentication.
     * @param options.silentAuthenticationUrl URL for silent login.
     */
    constructor(options) {
        this.loginIntervalTime = 200;
        this.iframeManager = null;
        this.accessTokenUrl = options.accessTokenUrl;
        this.authenticationUrl = options.authenticationUrl;
        this.clientId = options.clientId;
        this.locale = options.locale;
        this.loginOptions = options.loginOptions || {};
        this.logoutUri = options.logoutUri;
        this.logoutRedirectUri = options.logoutRedirectUri || '';
        this.redirectUri = options.redirectUri;
        this.silentAuthenticationUrl = options.silentAuthenticationUrl;
    }
    /**
     * Returns a logged in user IAccountServiceUser if the user is logged in, otherwise null.
     *
     * @param options ...
     * @param options.useSilentAuthentication When set to true, the user will be silently authenticated if the user is already logged in via DEXFs login script.
     * @returns A IAccountServiceUser if the user is logged in, otherwise null.
     */
    async getUser(options) {
        let tokens = null;
        const authenticationInProgress = DexfAccountServiceStorage.getAuthenticationState();
        if (authenticationInProgress) {
            tokens = await this.getTokensFromAuthInProgress();
        }
        if (!tokens) {
            if (options?.useSilentAuthentication) {
                const user = await this.handleSilentAuthentication();
                return user ?? null;
            }
            // User is not logged in
            return null;
        }
        return DexfAccountServiceUtility.createUserFromTokens(tokens);
    }
    async handleRedirection(url, usePopup, popupOptions) {
        if (usePopup && popupOptions) {
            const popup = this.openPopup(url, popupOptions.width, popupOptions.height, popupOptions.name);
            if (popup) {
                // If the popup was opened successfully, handle the authentication through the popup
                const tokens = await this.handlePopupAuthentication(popup);
                return DexfAccountServiceUtility.createUserFromTokens(tokens);
            }
            else {
                // If the popup failed to open, throw an error
                throw new ServiceException('Popup failed to open or was blocked by the browser.');
            }
        }
        else {
            this.redirectWindow(url);
        }
    }
    openPopup(url, width, height, name) {
        const left = (screen.width - width) / 2;
        const top = (screen.height - height) / 2;
        return window.open(url, name, `width=${width},height=${height},left=${left},top=${top}`);
    }
    redirectWindow(url) {
        // Redirect the current window to the authentication URL
        // If app is running in an iframe we need to redirect the parent url
        try {
            if (IframeUtility.isInsideIframe()) {
                // Planner can be running on a different domain than the parent page which means that it will not be possible to redirect
                if (IframeUtility.isParentWindowAccessible()) {
                    // See comment further down for non-iframe redirect.
                    setTimeout(() => {
                        window.parent.location.href = url;
                    }, 100);
                }
                else {
                    throw new ServiceException('Failed to redirect to the parent window because it was not accessible. Use the usePopup option instead.');
                }
            }
            else {
                // Browsers can in some cases (KOMPIS-6062) block redirects if they are too close together, seen in Safari and Chrome on iOS. Opening an iframe counts as a redirect as well.
                // By delaying the redirect here we're reducing the risk of it become blocked.
                setTimeout(() => {
                    window.location.href = url;
                }, 100);
            }
        }
        catch (error) {
            throw new ServiceException('Error during window redirection:' + error);
        }
    }
    async logIn(options) {
        // Destructure options with default values.
        const { usePopup = false, popupOptions: { width = 800, height = 640, name = 'oAuthPopup' } = {} } = this.loginOptions;
        try {
            // Clear all tokens in storage
            DexfAccountServiceStorage.clearAllTokens();
            // Create a verifier for PKCE
            const verifier = DexfAccountServiceUtility.base64(randomBytes(32));
            // Store the verifier in storage for later use
            DexfAccountServiceStorage.storePkce(verifier);
            // Create a code challenge from the verifier
            const challenge = DexfAccountServiceUtility.base64(DexfAccountServiceUtility.createSha256Hash(verifier));
            // Create the data payload for the authentication request
            const data = {
                redirectUri: this.redirectUri,
                responseType: 'code',
                clientId: this.clientId,
                codeChallenge: challenge,
                codeChallengeMethod: 'S256',
                ...(options.state && { state: options.state })
            };
            // Build the authentication URL
            const parameters = DexfAccountServiceUtility.createUrlParameters(data);
            const authUrl = `${this.authenticationUrl}&${parameters}`;
            DexfAccountServiceStorage.storeAuthenticationState(true);
            // Handle redirection. This will either return a token if a popup is used, or redirect the user, with the rest handled by the consumer.
            return this.handleRedirection(authUrl, usePopup, { width, height, name });
        }
        catch (error) {
            throw new ServiceException('Error during login process:' + error);
        }
    }
    async logOut() {
        // Validate the logout URI if it is defined since it is required for the logout process.
        if (!this.logoutRedirectUri?.trim()) {
            throw new ServiceException('Logout redirect URI is not defined.');
        }
        const data = {
            clientId: this.clientId,
            logoutUri: this.logoutRedirectUri,
            retailUnit: LocaleUtility.getCountryAndLanguage(this.locale).country?.toUpperCase()
        };
        const parameters = DexfAccountServiceUtility.createUrlParameters(data);
        const logoutUrl = `${this.logoutUri}&${parameters}`;
        // Clear all tokens before redirecting for logout to ensure the user is signed out.
        // Even if the logout process fails on the web, the user will be logged out in the planner.
        DexfAccountServiceStorage.clearAllTokens();
        // This will redirect to the logout and then redirect back to the logoutRedirectUri, which is the partial redirect URL for logout that consumers need to add in settings.
        this.handleRedirection(logoutUrl);
    }
    /**
     * Fetches the access and id tokens using authorization code.
     *
     * @param code ...
     * @returns The tokens.
     */
    async fetchTokens(code) {
        const data = {
            grantType: 'authorization_code',
            code: code,
            redirectUri: this.redirectUri,
            clientId: this.clientId,
            codeVerifier: DexfAccountServiceStorage.getPkce()
        };
        const body = DexfAccountServiceUtility.createUrlParameters(data);
        const response = await ServiceFetch.json(this.accessTokenUrl, {
            method: 'post',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: body
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!response.id_token || !response.access_token || response.code) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            throw new ServiceException('Failed to get access token. Error: ' + response.code);
        }
        // If the response is successful, store the tokens in storage and remove the code verifier and authentication state.
        DexfAccountServiceStorage.removeAuthenticationState();
        DexfAccountServiceStorage.removeCodeVerifier();
        DexfAccountServiceStorage.storeTokens(response);
        return { accessToken: response.access_token, idToken: response.id_token };
    }
    async getTokensFromAuthInProgress() {
        const codeVerifier = DexfAccountServiceUtility.getCodeSearchParam();
        DexfAccountServiceStorage.removeAuthenticationState();
        if (codeVerifier) {
            return await this.fetchTokens(codeVerifier);
        }
        return null;
    }
    async createIframe(url) {
        if (this.iframeManager && this.iframeManager.url !== url) {
            this.iframeManager.disconnect();
            this.iframeManager = null;
        }
        if (!this.iframeManager) {
            this.iframeManager = new IframeManager(url);
        }
        return await this.iframeManager.getIframe();
    }
    async handleSilentAuthentication() {
        const iframe = await this.createIframe(this.silentAuthenticationUrl);
        const iframeResponse = await this.readIframeContext(iframe);
        if (iframeResponse.error !== null) {
            return null;
        }
        const tokens = {
            accessToken: iframeResponse.token.accessToken,
            idToken: iframeResponse.token.idToken
        };
        const user = DexfAccountServiceUtility.createUserFromTokens(tokens);
        return user;
    }
    /**
     * Monitors the authentication process.
     *
     * @param popup Optional popup window for authentication.
     * @returns The access token.
     */
    handlePopupAuthentication(popup) {
        return new Promise((resolve, reject) => {
            const intervalId = setInterval(async () => {
                // If the popup is open and has "error" in the URL, close it.
                if (popup && !popup.closed) {
                    try {
                        const params = new URLSearchParams(popup.location.search);
                        if (params.has('error')) {
                            popup.close();
                        }
                    }
                    catch (e) {
                        // When reading the url in the popup, it will throw a CORS domain SecurityError.
                        // We can ignore this error since had to read the url to check if there is an error or not.
                        if (e.name !== 'SecurityError') {
                            console.error(e);
                        }
                    }
                }
                try {
                    if (popup && popup.closed) {
                        DexfAccountServiceStorage.removeAuthenticationState();
                        clearInterval(intervalId);
                        reject(new ServiceException('Failed to login. The popup was closed.'));
                        return;
                    }
                    // Check if user tokens have been stored after logging in.
                    const accessToken = DexfAccountServiceStorage.getToken(AccountServiceEnum.accessToken);
                    const idToken = DexfAccountServiceStorage.getToken(AccountServiceEnum.idToken);
                    if (accessToken && idToken) {
                        if (popup) {
                            popup.close();
                        }
                        resolve({ accessToken, idToken });
                        clearInterval(intervalId);
                    }
                }
                catch (e) {
                    clearInterval(intervalId);
                    reject(new ServiceException(`Failed to login with popup: ${e.message}`));
                }
            }, this.loginIntervalTime);
        });
    }
    async readIframeContext(iframe) {
        const dexfUrlOrigin = new URL(this.silentAuthenticationUrl).origin;
        return await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                window.removeEventListener('message', messageListener);
                reject(new ServiceException('No response from DEXF silent-authentication iframe script. Connection timed out.'));
            }, 10000);
            const messageListener = (event) => {
                if (event.origin !== dexfUrlOrigin) {
                    return;
                }
                let parsedData;
                try {
                    parsedData = event.data;
                }
                catch (error) {
                    return;
                }
                if (parsedData.messageType === 'silent-login-response') {
                    window.removeEventListener('message', messageListener);
                    clearTimeout(timeout);
                    const formattedResponse = {
                        messageType: parsedData.messageType,
                        loggedin: parsedData.loggedin,
                        error: parsedData.error,
                        ...(parsedData.token && {
                            token: {
                                accessToken: parsedData.token.access_token,
                                idToken: parsedData.token.id_token
                            }
                        })
                    };
                    resolve(formattedResponse);
                }
            };
            window.addEventListener('message', messageListener);
            const data = {
                messageType: 'silent-login-start',
                retailUnit: LocaleUtility.getCountryAndLanguage(this.locale).country?.toUpperCase(),
                locale: this.locale
            };
            iframe.contentWindow?.postMessage(data, dexfUrlOrigin);
        });
    }
}
//# sourceMappingURL=DexfAccountService.js.map